import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import Modal from '../common/Modal'
import { handleGetRequest } from '../../utils/api-handler.util'
import useProductCategories from '../../hooks/useProductCategories'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { useAuth } from '../../contexts/AuthContext'
import { useFilters } from '../../hooks/useFilters'
import { FILTER_TYPES } from '../../constants/filters'

const RetailProductPickerModal = ({ onClose, onSelect }) => {
  const { currentUser } = useAuth()
  const { filters, renderFilters, renderPagination } = useFilters(
    {},
    { [FILTER_TYPES.search]: 'Search for products' }
  )
  const [selectedProductId, setSelectedProductId] = useState(null)
  const { getCategoryNameById } = useProductCategories()

  const { data: productsData, isLoading: loading } = useQuery({
    queryKey: QueryKeys.availableProducts(filters),
    queryFn: () => handleGetRequest(`${currentUser?.userType}s/available-products`, filters),
  })

  const products = productsData?.data || []

  const handleProductSelect = (product) => {
    onSelect(product)
    setSelectedProductId(product.id)
    onClose()
  }

  const renderProduct = (product) => {
    return (
      <div
        key={product.id}
        className={`relative rounded-md bg-background border px-2 py-2 hover:border-amber-500 cursor-pointer ${
          selectedProductId === product.id ? 'border-amber-500' : 'border-gray-100'
        }`}
        onClick={() => handleProductSelect(product)}
      >
        <img
          src={product.primaryImageUrl}
          alt={product.name}
          className="w-full aspect-square object-cover rounded"
        />
        <div className="flex justify-between items-center mt-2">
          <div className="flex-1">
            <div className="text-black text-sm font-medium text-center px-1">{product.name}</div>
            <div className="text-xs text-center text-gray-500 px-1">
              {getCategoryNameById(product.categoryId) || 'Uncategorized'}
            </div>
            <div className="text-xs text-center text-gray-500 px-1">{product.priceText}</div>
          </div>
          <CheckCircleIcon
            className={`w-6 h-6 text-amber-500 ${
              selectedProductId === product.id ? 'visible' : 'invisible'
            }`}
          />
        </div>
      </div>
    )
  }

  const renderContent = () => {
    if (loading) {
      return <div className="text-center text-gray-500 py-8">Loading products...</div>
    }

    if (products.length === 0) {
      return (
        <div className="text-center text-gray-500 py-8">
          No retail products available at the moment.
          <br />
          Please try again later or add a new product.
        </div>
      )
    }

    return (
      <>
        <div className="grid grid-cols-2 gap-3 mb-8 max-h-96 overflow-y-auto">
          {products.map((product) => renderProduct(product))}
        </div>
        {renderPagination(productsData?.meta)}
      </>
    )
  }

  return (
    <Modal isOpen={true} onClose={onClose} styleClass="max-w-2xl">
      <h2 className="text-center text-2xl font-bold mb-6">Select a Product to Resell</h2>
      <p className="text-center text-gray-600 mb-6">
        Choose from existing products and set your selling price
      </p>
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {renderFilters([FILTER_TYPES.categories, FILTER_TYPES.search])}
      </div>
      {renderContent()}
    </Modal>
  )
}

export default RetailProductPickerModal
